/**
 * Reading the catalog.
 *
 * Pure functions over the static data: no fetching, no ranking model, no
 * personalisation. A shelf that comes back empty is a fact about the platform,
 * and the portal renders it as one.
 */

import { CATEGORIES } from "./categories.js";
import { GAMES } from "./games.js";
import { MOODS } from "./moods.js";
import type { Category, CategoryId, GameEntry, Mood, MoodId } from "./types.js";

export function allGames(): readonly GameEntry[] {
  return GAMES;
}

export function playableGames(): readonly GameEntry[] {
  return GAMES.filter((game) => game.status === "playable");
}

export function featuredGames(): readonly GameEntry[] {
  return GAMES.filter((game) => game.featured && game.status === "playable");
}

export function getGame(slug: string): GameEntry | null {
  return GAMES.find((game) => game.slug === slug) ?? null;
}

export function gamesInCategory(id: CategoryId): readonly GameEntry[] {
  return GAMES.filter((game) => game.category === id);
}

export function gamesWithMood(id: MoodId): readonly GameEntry[] {
  return GAMES.filter((game) => game.moods.includes(id));
}

/** A shelf is "coming soon" until something is actually playable on it. */
export function categoryStatus(id: CategoryId): "open" | "coming-soon" {
  return gamesInCategory(id).some((game) => game.status === "playable") ? "open" : "coming-soon";
}

export function moodStatus(id: MoodId): "open" | "coming-soon" {
  return gamesWithMood(id).some((game) => game.status === "playable") ? "open" : "coming-soon";
}

export interface SearchHit {
  kind: "game" | "category" | "mood";
  id: string;
  /** The i18n key whose translation matched, so the caller can show it. */
  nameKey: string;
}

/**
 * Search across games, shelves and moods.
 *
 * The catalog stores keys, not words, so the caller passes a `label` function
 * that renders a key in the reader's language; matching then happens on what
 * the visitor can actually see. Empty query means "no results yet", not
 * "everything": the portal already shows everything below the search field.
 */
export function searchCatalog(
  query: string,
  label: (key: string) => string,
  limit = 8,
): readonly SearchHit[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [];

  const hits: SearchHit[] = [];
  const push = (kind: SearchHit["kind"], id: string, nameKey: string, extra = ""): void => {
    const haystack = `${label(nameKey)} ${id} ${extra}`.toLowerCase();
    if (haystack.includes(needle)) hits.push({ kind, id, nameKey });
  };

  for (const game of GAMES) push("game", game.slug, game.titleKey, label(game.taglineKey));
  for (const category of CATEGORIES as readonly Category[]) {
    push("category", category.id, category.nameKey, label(category.blurbKey));
  }
  for (const mood of MOODS as readonly Mood[]) push("mood", mood.id, mood.nameKey);

  return hits.slice(0, limit);
}
